import React, { Fragment, useState } from "react";
import config from "../../config/config";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import { Img, Picture } from "onnit-ui/lib/image";
import Grid from "onnit-ui/lib/module/grid/12/Grid";
import Box from "onnit-ui/lib/box/Box";
import Text, { MultiColumnText, Sup } from "onnit-ui/lib/text";
import SupplementFacts from "../../components/common/SupplementFacts";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";

import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import NewMoodIngredientSlider from "../new-mood/NewMoodIngredientSlider";
import NewMoodIngredientList from "../new-mood/NewMoodIngredientList";

import MediaHero01 from "onnit-ui/lib/module/MediaHero01";
import HeadlineLabelContent from "onnit-ui/lib/module/HeadlineLabelContent";
import Embed from "onnit-ui/lib/video/Embed";
import MediaCollage03 from "onnit-ui/lib/module/MediaCollage03";
import MediaCollage01 from "onnit-ui/lib/module/MediaCollage01";
import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import UpSell from "../../components/common/UpSell";

function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) return null;

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/${pageData.slug}`;
    const IMG_PARAMS = config.IMG_PARAMS;
    const purple = "#5D5BA8";

    // Upsell Flavors
    const PINEAPPLE = findProduct(8285);
    const GUAVA = findProduct(8287);
    const MANGO = findProduct(8289);
    const ELDER = findProduct(8291);

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    productNameTransform={(name) => {
                        return name.replace(/New MOOD® Instant - /gi, "");
                    }}
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/BuyBox-1-mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/BuyBox-1.jpg${IMG_PARAMS}`,
                            ]}
                            alt="A hand reaching for New MOOD® Instant on a night stand"
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-2.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="New MOOD® Instant Orange Guava Packaging"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/BuyBox-3.jpg${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="New MOOD® Instant Mango Packaging"
                            width={928}
                            height={928}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/BuyBox-4-mobile.jpg${IMG_PARAMS}`,
                                `${IMG_URL}/BuyBox-4.jpg${IMG_PARAMS}`,
                            ]}
                            alt="All New MOOD® Instant Flavors in front of fruit"
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box backgroundImage="linear-gradient(#534F96, #C6B1D0)" py={[6, 6, 8, 10]} mt={[0, 0, 8, 10]}>
                        <MediaHero01
                            mb={[6, 6, 8, 10]}
                            media={
                                <Img
                                    src={`${IMG_URL}/MediaHero-1-mobile.jpg${IMG_PARAMS}`}
                                    alt="lavender plants"
                                    width={460}
                                    height={460}
                                />
                            }
                            headline={
                                <Fragment>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Balance
                                    </Text>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Daily
                                    </Text>
                                    <Text typeStyle="headlineHero01" color="white">
                                        Stress
                                    </Text>
                                </Fragment>
                            }
                        />

                        <Grid px={[4, 4, 5]} mb={4}>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 9"]}>
                                <Text typeStyle="copyHero01" color="white" as="p">
                                    New MOOD
                                    <Sup variant="reg" /> Instant is the amp antidote, helping you relax and focus on
                                    yourself by turning down the noise of the world
                                    <Sup />
                                </Text>
                            </Box>
                        </Grid>

                        <HeadlineLabelContent
                            py={[6, 6, 8, 10]}
                            label={<Text typeStyle="copy05">Daily Stress</Text>}
                            headline={
                                <Text as="h2" typeStyle="headlineHero02" fontStyle="normal">
                                    Slow Your Roll
                                </Text>
                            }
                            content={
                                <Text typeStyle="copyHero02" as="p">
                                    Designed to be used morning, day, or night for helping to balance your daily
                                    stresses.
                                    <Sup />
                                </Text>
                            }
                        />

                        <Grid>
                            <Box gridColumn={["1 / -1", "1/-1", "1/-1", "3 / span 8"]} px={4}>
                                <Embed
                                    title="New MOOD® Instant product video"
                                    src="https://player.vimeo.com/video/513104826"
                                />
                            </Box>
                        </Grid>
                    </Box>

                    <MediaCollage03
                        py={[6, 6, 8, 10]}
                        item1={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-2.jpg${IMG_PARAMS}`}
                                alt="woman waking up refreshed"
                                width={585}
                                height={328}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-1.jpg${IMG_PARAMS}`}
                                alt="pineapple"
                                width={448}
                                height={448}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/MediaCollage-1-3.jpg${IMG_PARAMS}`}
                                alt="Man drinking New MOOD® Instant"
                                width={688}
                                height={388}
                            />
                        }
                    />

                    <Box display="grid" gridTemplateColumns="repeat(12, 1fr)">
                        <Box gridColumn={["1 / -1", "2 / -1"]} gridRow={1} px={4}>
                            <Text as="h2" typeStyle="headlineHero01" color={purple} mb={[5, 8]}>
                                How <br />
                                New MOOD
                                <Sup variant="reg" />
                                <br />
                                Instant <br />
                                Works
                            </Text>
                        </Box>

                        <Box
                            bg={["transparent", "rgba(198,177,208,0.2)"]}
                            gridColumn={["1 / -1", "4 / span 8"]}
                            gridRow={2}
                            p={[0, 5, 6]}
                            px={4}
                        >
                            <Text as="p" typeStyle="copyHero02" fontWeight="regular">
                                Serotonin has been shown to be a key neurotransmitter linked to mood, happiness, and
                                positive outlook. Combining the two raw building blocks of serotonin, L-tryptophan and
                                5-HTP, with a converting catalyst, Vitamin B6, New MOOD
                                <Sup variant="reg" /> Instant provides the nutrients to help the body optimize serotonin
                                levels.
                                <Sup /> <br />
                                <br />
                                Adding to the naturally relaxing effects of L-Tryptophan are a host of traditional herbs
                                designed to help you maintain a state of peaceful calm.
                                <Sup /> Ideal for when you are under stress, down in the dumps, or just need some
                                refreshing sleep, New MOOD
                                <Sup variant="reg" /> Instant is a great product to take morning, afternoon, or night.
                                <Sup />
                            </Text>
                        </Box>
                    </Box>

                    <MediaCollage01
                        py={[6, 6, 8, 10]}
                        item1={
                            <Img
                                src={`${IMG_URL}/MediaCollage-2-1.jpg${IMG_PARAMS}`}
                                alt="mangos"
                                width={448}
                                height={692}
                            />
                        }
                        item2={
                            <Img
                                src={`${IMG_URL}/MediaCollage-2-2.jpg${IMG_PARAMS}`}
                                alt="oranges"
                                width={448}
                                height={692}
                            />
                        }
                        item3={
                            <Img
                                src={`${IMG_URL}/MediaCollage-2-3.jpg${IMG_PARAMS}`}
                                alt="Woman stirring New MOOD® Instant"
                                width={928}
                                height={1280}
                            />
                        }
                    />

                    <CopyDisplay02 dashColor="#FF4438">
                        <Text typeStyle="copyDisplay02" mb={6} color={purple}>
                            What's The Difference Between Regular New MOOD
                            <Sup variant="reg" /> and New MOOD
                            <Sup variant="reg" /> Instant?
                        </Text>
                        <MultiColumnText>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={4}>
                                Both versions of New MOOD
                                <Sup variant="reg" /> contain the same ingredients. The only difference is how they're
                                packaged. Original New MOOD
                                <Sup variant="reg" /> comes in a capsule, making it convenient for those who just want
                                to swallow a pill. New MOOD
                                <Sup variant="reg" /> Instant, on the other hand, is for those who would prefer to
                                consume their supplements as a tasty beverage. To that end, New MOOD
                                <Sup variant="reg" /> Instant comes as a powder that can be mixed into a glass of water,
                                and it's available in four delicious flavors&mdash;Mango, Orange Guava, Elderberry
                                Lavender, and Pineapple.
                            </Text>
                        </MultiColumnText>
                    </CopyDisplay02>

                    {MANGO && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={MANGO.id}
                            price={MANGO.price}
                            inStock={MANGO.in_stock}
                            msrp={MANGO.price_msrp}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Mango.jpg${IMG_PARAMS}`}
                                    alt={MANGO.name}
                                    width={460}
                                    height={460}
                                />
                            }
                            title={<>Mango</>}
                            subtext="30 count"
                            description={
                                <>
                                    When you’re basking on the beach in a tropical paradise, you’re not drinking your
                                    favorite energy drink… There may be no other fruit more closely associated with
                                    relaxation.
                                </>
                            }
                        />
                    )}

                    {GUAVA && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={GUAVA.id}
                            price={GUAVA.price}
                            inStock={GUAVA.in_stock}
                            msrp={GUAVA.price_msrp}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Guava.jpg${IMG_PARAMS}`}
                                    alt={GUAVA.name}
                                    width={460}
                                    height={460}
                                />
                            }
                            title={<>Orange Guava</>}
                            subtext="30 count"
                            description={<>Citrusy and sweet, this combo brings the tropics to your taste buds.</>}
                        />
                    )}

                    {ELDER && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={ELDER.id}
                            price={ELDER.price}
                            inStock={ELDER.in_stock}
                            msrp={ELDER.price_msrp}
                            showPercentageSaved={true}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Elder.jpg${IMG_PARAMS}`}
                                    alt={ELDER.name}
                                    width={460}
                                    height={460}
                                />
                            }
                            title={<>Elderberry Lavender</>}
                            subtext="30 count"
                            description={
                                <>
                                    Each half of this purple pair is associated with calmness by itself. Combined with
                                    New MOOD
                                    <Sup variant="reg" /> Instant’s ingredients, and maybe some Yacht Rock Radio, you’ll
                                    drift away...
                                </>
                            }
                        />
                    )}

                    {PINEAPPLE && (
                        <UpSell
                            textColor="black"
                            buttonColor="black"
                            buttonTextColor="white"
                            pid={PINEAPPLE.id}
                            price={PINEAPPLE.price}
                            inStock={PINEAPPLE.in_stock}
                            showPercentageSaved={true}
                            msrp={PINEAPPLE.price_msrp}
                            img={
                                <Img
                                    src={`${IMG_URL}/Flavor-Pineapple.jpg${IMG_PARAMS}`}
                                    alt="New MOOD® Instant Pineapple"
                                    width={460}
                                    height={460}
                                />
                            }
                            title={<>Pineapple</>}
                            subtext="30 count"
                            description={
                                <>
                                    Who doesn’t want a pina colada when they’re stressed? Tiny parasol (and booze) not
                                    included.
                                </>
                            }
                        />
                    )}

                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <Grid>
                            <Box px={4} py={6} gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / 12"]}>
                                <Text typeStyle="headlineHero02" color="white" fontSize={[6, 6, 6, 8, 8]}>
                                    What's inside New MOOD
                                    <Sup variant="reg" /> Instant?
                                </Text>
                            </Box>
                        </Grid>
                        <NewMoodIngredientSlider />
                    </Box>

                    <NewMoodIngredientList
                        py={[6, 6, 8]}
                        description={
                            <Text as="p" typeStyle="copyHero01" color="black">
                                These ingredients round out New MOOD
                                <Sup variant="reg" /> Instant's relaxing formula.
                            </Text>
                        }
                    />
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={<SupplementFacts currentPID={currentPID} />} />
                )}
            </>
        </ThemeProvider>
    );
}

export default Page;
